import { Edge, Node, useReactFlow } from '@xyflow/react';
import { useMemo } from 'react';
import FormInput from 'src/components/form-input';
import FormSelect from 'src/components/form-select';
import {
  normalizeCablePair,
  connectionTypeToCableConnector,
} from 'src/modules/network-configuration/models/connection-utils';
import {
  EDataType,
  NodeCategoryModel,
  EPortConnectionType,
} from 'src/modules/network-configuration/models/node-data';
import { getEdgeInfo } from 'src/utils/node';

import { ECableType, IEdgeExtraData, CABLE_TYPE_LABELS } from '../types';

const FALLBACK_CONNECTOR_INFO = {
  isSwitchToSwitch: false,
  switchConnector: undefined as ECableType | undefined,
  targetSwitchConnector: undefined as ECableType | undefined,
  deviceOptions: [ECableType.X, ECableType.D, ECableType.RJ45],
  defaultDeviceType: ECableType.RJ45,
};

function getPortConnectionType(
  category: NodeCategoryModel | undefined,
  portId: string | undefined,
): EPortConnectionType | undefined {
  if (!portId) return undefined;
  return (category?.ports || []).find((p) => p.id === portId)?.connectionType;
}

export function CustomEdgeEditCableType({
  data,
  onChangeEdgeData,
  edgeIds,
}: {
  data: IEdgeExtraData;
  onChangeEdgeData: (data: Partial<IEdgeExtraData>) => void;
  edgeIds: string[];
}) {
  const { getEdges, getNodes } = useReactFlow();

  const connectorInfo = useMemo(() => {
    if (!edgeIds?.length) {
      return FALLBACK_CONNECTOR_INFO;
    }

    const edge = (getEdges() as Edge[]).find((e) => edgeIds.includes(e.id));
    if (!edge) {
      return FALLBACK_CONNECTOR_INFO;
    }

    const { sourceNode, targetNode, sourcePort, targetPort } = getEdgeInfo(
      edge,
      getNodes() as Node[],
    );

    const sourceCategory = sourceNode?.data?.category as
      | NodeCategoryModel
      | undefined;
    const targetCategory = targetNode?.data?.category as
      | NodeCategoryModel
      | undefined;

    const isSourceSwitch = sourceCategory?.type === EDataType.Switch;
    const isTargetSwitch = targetCategory?.type === EDataType.Switch;

    if (isSourceSwitch && isTargetSwitch) {
      const sourcePortType = getPortConnectionType(sourceCategory, sourcePort);
      const targetPortType = getPortConnectionType(targetCategory, targetPort);
      const switchConnector = sourcePortType
        ? connectionTypeToCableConnector(sourcePortType)
        : undefined;
      const targetSwitchConnector = targetPortType
        ? connectionTypeToCableConnector(targetPortType)
        : undefined;

      if (!switchConnector || !targetSwitchConnector) {
        return FALLBACK_CONNECTOR_INFO;
      }

      const normalized = normalizeCablePair(
        switchConnector,
        targetSwitchConnector,
      );

      return {
        isSwitchToSwitch: true,
        switchConnector: normalized.switchConnector,
        targetSwitchConnector: normalized.deviceConnector,
        deviceOptions: [],
        defaultDeviceType: normalized.deviceConnector,
      };
    }

    const switchPortType = isSourceSwitch
      ? getPortConnectionType(sourceCategory, sourcePort)
      : getPortConnectionType(targetCategory, targetPort);
    const switchConnector = switchPortType
      ? connectionTypeToCableConnector(switchPortType)
      : undefined;

    if (!switchConnector) {
      return FALLBACK_CONNECTOR_INFO;
    }

    const deviceOptions =
      switchConnector === ECableType.X
        ? [ECableType.X, ECableType.RJ45]
        : [ECableType.D, ECableType.RJ45];

    return {
      isSwitchToSwitch: false,
      switchConnector,
      targetSwitchConnector: undefined,
      deviceOptions,
      defaultDeviceType: deviceOptions.includes(ECableType.RJ45)
        ? ECableType.RJ45
        : deviceOptions[0],
    };
  }, [edgeIds, getEdges, getNodes]);

  const switchConnector = connectorInfo.switchConnector ?? ECableType.X;
  const baseCable = data?.cableType ?? {
    switchConnector,
    deviceConnector: connectorInfo.defaultDeviceType,
    length: 0,
  };

  const singleCable = connectorInfo.isSwitchToSwitch
    ? {
        ...baseCable,
        switchConnector,
        deviceConnector: connectorInfo.targetSwitchConnector ?? switchConnector,
      }
    : baseCable;

  const handleUpdateCableType = (
    field: 'deviceConnector' | 'length',
    value: ECableType | number,
  ) => {
    onChangeEdgeData({ cableType: { ...singleCable, [field]: value } });
  };

  const deviceSelectOptions = useMemo(
    () =>
      (connectorInfo.deviceOptions.length > 0
        ? connectorInfo.deviceOptions
        : FALLBACK_CONNECTOR_INFO.deviceOptions
      ).map((type) => ({
        label: CABLE_TYPE_LABELS[type],
        value: type,
      })),
    [connectorInfo.deviceOptions],
  );

  return (
    <div
      className="mt-2 flex flex-col items-start justify-center gap-4"
      data-testid="dialog-custom-edge-edit-cable-type-panel"
    >
      <div className="flex w-full items-center justify-between">
        <span className="block text-left text-sm font-medium leading-6 text-gray-900">
          Cable Type
        </span>
      </div>
      <div className="w-full overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300 border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-semibold text-gray-900"
              >
                Switch connector
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-semibold text-gray-900"
              >
                Length (meter)
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-semibold text-gray-900"
              >
                Device connector
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            <tr className="hover:bg-gray-50">
              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">
                {connectorInfo.switchConnector
                  ? CABLE_TYPE_LABELS[connectorInfo.switchConnector]
                  : 'N/A'}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm">
                <FormInput
                  data-testid="dialog-custom-edge-edit-cable-type-length-input"
                  id="cable-length"
                  name="cableLength"
                  type="number"
                  value={
                    singleCable.length !== undefined
                      ? String(Math.floor(Number(singleCable.length)))
                      : '0'
                  }
                  wrapperClassName="w-full"
                  className="w-full"
                  onChange={(e) => {
                    const raw = e.target.value;
                    const parsed =
                      raw === '' ? 0 : Math.max(0, parseInt(raw, 10) || 0);
                    handleUpdateCableType('length', parsed);
                  }}
                  placeholder="Length (integer)"
                  min="0"
                  step="1"
                />
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm">
                {connectorInfo.isSwitchToSwitch ? (
                  <span>{CABLE_TYPE_LABELS[singleCable.deviceConnector]}</span>
                ) : (
                  <FormSelect
                    data-testid="dialog-custom-edge-edit-cable-type-device-connector-select"
                    id="cable-device-connector"
                    className="w-full"
                    options={deviceSelectOptions}
                    value={
                      singleCable.deviceConnector ||
                      connectorInfo.defaultDeviceType
                    }
                    onChange={(e) =>
                      handleUpdateCableType(
                        'deviceConnector',
                        e.target.value as ECableType,
                      )
                    }
                  />
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
